import Axios from 'axios';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

import { authActions, authSelectors } from 'app-components/flux/auth';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  crashReporterInterface,
  getEnvironmentConfig,
  getHostConfig,
  getLogger,
  localPreferences,
  tracker,
} from 'app-components/utils/core';
import { isDemoMode } from 'app-components/utils/core';
import { getRequestFailureMetadata } from 'app-components/utils/core/axiosErrorGrouping';
import { isDevMode } from 'app-components/utils/core/config';
import { getSafeCode, normalizePath, scrubUrl } from 'app-components/utils/core/telemetrySafe';
import store from 'app-components/utils/redux-store';

import { getIsOffline, isSanitized } from 'data/app/selectors';
import * as sessionStorageEx from 'utils/sessionStorageEx';
import { getRoutingHeaderForPath } from 'utils/devRouting';

const log = getLogger('utils/axiosFactory.js');

const AxiosFactory = {
  registeredAxiosInstances: new Map(),

  register(axiosInstanceName, axiosInstance) {
    if (!AxiosFactory.registeredAxiosInstances.has(axiosInstanceName)) {
      AxiosFactory.registeredAxiosInstances.set(axiosInstanceName, axiosInstance);
    } else {
      log.error('This axios instance has already been registered ', axiosInstance);
    }
  },

  get(axiosInstanceName) {
    return AxiosFactory.registeredAxiosInstances.get(axiosInstanceName);
  },

  unregister(axiosInstanceName) {
    log.log(`AxiosFactory.unregister(${axiosInstanceName})`);
    if (AxiosFactory.registeredAxiosInstances.has(axiosInstanceName)) {
      delete AxiosFactory.registeredAxiosInstances[axiosInstanceName];
    }
  },
};

// /////////////////////////////////////////////////////////////////////////////
// Setup Axios Instance for QCS
// /////////////////////////////////////////////////////////////////////////////

const qcsAxiosInstance = Axios.create();
export const cancelTokenSource = Axios.CancelToken.source();

qcsAxiosInstance.defaults.cancelToken = cancelTokenSource.token;
qcsAxiosInstance.defaults.headers.common['app-client-id'] = getEnvironmentConfig().client_id;
qcsAxiosInstance.defaults.headers.common['app-release'] = process.env.APP_VERSION;
qcsAxiosInstance.defaults.headers.common['app-build'] = process.env.BUILD_NUMBER;
qcsAxiosInstance.defaults.headers.patch['Content-Type'] = 'application/json;charset=UTF-8';
qcsAxiosInstance.defaults.headers.post['Content-Type'] = 'application/json;charset=UTF-8';
qcsAxiosInstance.defaults.headers.put['Content-Type'] = 'application/json;charset=UTF-8';

qcsAxiosInstance.interceptors.request.use(
  (config) => {
    let configOrPromise = config;

    if (config.cancelToken && config.cancelToken.throwIfRequested) {
      config.cancelToken.throwIfRequested();
    }

    if ((getIsOffline(store.getState()) || !navigator.onLine) && localPreferences.getOfflineSupport()) {
      return Promise.reject(Error('offline'));
    }

    // Ensure request headers are set correctly
    configOrPromise.headers['client-tid'] = configOrPromise.headers['client-tid'] || uuidv4().toUpperCase();

    configOrPromise = handleAuthorization(configOrPromise);

    return configOrPromise;
  },
  (error) => {
    log.info('qcsAxiosInstance.interceptors.request.use ERROR');
    return Promise.reject(error);
  },
);

qcsAxiosInstance.interceptors.response.use(
  (response) => {
    // log.debug('Axios Response:', response);
    const itemWithError =
      response.data && response.data.items && response.data.items.find((item) => item.errors && item.errors.length > 0);
    if (itemWithError) {
      const error = itemWithError.errors?.find((err) => err) || {};
      const url = new URL(response.config && response.config.url);
      const tag = url && url.pathname && normalizePath(url.pathname);
      tracker.track(tracker.events.connectivity, {
        status: 'error',
        method: response.config && response.config.method && response.config.method.toUpperCase(),
        host: url && url.hostname,
        path: tag,
        tag,
        description: error.title,
        error_code: error.httpStatus,
        error_sub_code: error.code,
        details: error.title,
        request_size: response.config && response.config.data && response.config.data.length,
        response_size: response.request && response.request.response && response.request.response.length,
      });
      reportRequestFailure(
        'Request item failed',
        response.config,
        response,
        `${error.httpStatus}:${getSafeCode(error.code)}`,
      );
    }

    requestAttempts.delete(response.config.headers['client-tid']);

    return response;
  },
  (axiosError) => {
    if (Axios.isCancel(axiosError)) {
      if (axiosError?.config?.headers) {
        requestAttempts.delete(axiosError.config.headers['client-tid']);
      }
      return Promise.reject(axiosError);
    }

/////////////////////////////////////////////
    // Dev SSO failures stay local: do not log credentials or run normal auth recovery.
    if (axiosError?.config?.opaqueRefreshTokenRedemption) return Promise.reject(axiosError);
/////////////
    const error =
      axiosError.isAxiosError &&
      axiosError.name === 'AxiosError' &&
      axiosError.code === 'ERR_NETWORK' &&
      !axiosError.response &&
      axiosError.message === 'Network Error' &&
      navigator.onLine === false
        ? {
            ...axiosError,
            isAxiosError: false,
            name: 'ClientError',
            code: 'CLIENT_OFFLINE',
            message: 'Client Offline Error',
          }
        : axiosError;

    if (error.message === 'demoMode') {
      return Promise.reject(error);
    }
    log.warn('Axios ERROR: ', error.message, error.code, error.response?.status);
    const url = error.config && error.config.url && new URL(error.config.url);
    const tag = url && url.pathname && normalizePath(url.pathname);
    tracker.track(tracker.events.connectivity, {
      status: 'error',
      method: error.config && error.config.method && error.config.method.toUpperCase(),
      host: url && url.hostname,
      path: tag,
      tag,
      description: error.message,
      error_code: error.response?.status || error.message,
      error_sub_code: error.response?.data?.errors?.[0]?.code,
      details:
        error.response?.data?.errors?.[0]?.extData?.exception ||
        error.response?.data?.errors?.[0]?.detail ||
        error.response?.data?.errors?.[0]?.title ||
        error.response?.data?.explanation,
      request_size: error.config && error.config.data && error.config.data.length,
      response_size: error.request && error.request.response && error.request.response.length,
    });
    reportRequestFailure(
      error.message,
      error.config,
      error.response,
      `${error.response?.status || error.message}:${getSafeCode(error.response?.data?.errors?.[0]?.code) || error.code}`,
      error.code,
    );

    if (error.response && error.response.data) {
      if (isDemoMode() && error.response?.data?.code === 'QCS-0403-3') {
        return Promise.reject(Error('demoMode'));
      }

      if (tag === '/oauth/token' && error.response.status === 401) {
        // special handling for refresh token failure
        let request;
        try {
          request = JSON.parse(error.config.data);
        } finally {
          if (request.grantType === 'refreshToken') {
            // refresh token failed - invalidate
            store.dispatch(authActions.invalidateRefreshToken(undefined, { context: 'axios 401 refreshToken failed' }));
          }
        }
      }

      const qcsErrors = error.response.data.errors;
      if (qcsErrors) {
        log.warn('Received QCS application level errors', qcsErrors);

        // check if we have an authorization failure (will only have a single error if authentication fails)
        if (qcsErrors.length === 1) {
          const qcsError = qcsErrors[0];

          log.warn(`QcsError: [httpStatus=${qcsError.httpStatus}, code=${qcsError.code}, detail='${qcsError.detail}']`);
          if (qcsError.httpStatus === 401) {
            if (qcsError.code === 'QCS-0401-2') {
              const authSession = authSelectors.getAuthSession(store.getState());
              if (authSession && !isRefreshTokenExpired(authSession) && authSession.refreshToken) {
                const attempts = requestAttempts.get(error.config.headers['client-tid']);
                if (attempts < 2) {
                  doRefreshToken(authSession.refreshToken);
                  return retryRequestAfterRefreshingQcsToken(error.config);
                }
              }
            } else if (qcsError.code === 'QCS-0401-7') {
              // QCS Unauthorized Access - Username requires verification for community access
              return error.response;
            }
            // on any other 401 (besides token expired, redirect to login)
            log.log('Service returned a non-recoverable 401');

            if (
              qcsError.code === 'QCS-0401-5' || // Trying to access a deleted dataset.
              qcsError.code === 'QCS-0401-6' || // "QCS Unauthorized Access - Entity was not found [name=Dataset, id=188911040088047616, parameter=id]"
              qcsError.code === 'QCS-0401-4' // "QCS Unauthorized Access - Invalid Entity Access [name=Dataset, id=304249255459973889, ownerId=266549569629118976, AccessorId=46930212407357697]",
            ) {
              // The dataset being operated on has been deleted.  Panic!
              // verify(false, 'This dataset has been reset or deleted, you will need to login again.');
              store.dispatch(authActions.authSelectDataset({ dataset: null, reload: false, location: '/' }));
              return error.response;
            }

            // assert(false, `response auth error - force logout\n${error.config?.url}`);
            // store.dispatch(authActions.authLogout({ reason: 'AUTH_LOGOUT_UNAUTHORIZED' }));
            store.dispatch(authActions.invalidateAccessToken(undefined, { context: 'axios 401 not resolved' }));
          } else if (qcsError.httpStatus === 400) {
            if (qcsError.code === 'QCS-0400-19') {
              log.error('NO VALID DATASET ID RESPONSE - FORCE USER LOGIN');
              assert(false, `QCS-0400-19 - NO NO NO!!!\n${tag}`);
              store.dispatch(authActions.authSelectDataset({ dataset: null, reload: false, location: '/' }));
            }
          }
        }
      } else if (error.response.data.error) {
        log.log(`Received SpringBoot level error, error=${error.response.data.error}`);
        if (error.response.data.status === 404) {
          error.response.data.extra = 'Please try again. If this problem persists please contact QCS Engineering.';
        }
      } else if (Math.trunc(error.response.status / 100) === 4 || Math.trunc(error.response.status) / 100 === 5) {
        log.debug(`Received generic service level error, error=${error.response.status}`);
        error.response.data = {
          status: error.response.status,
          error: `Generic service error, error=${error.response.status}`,
          message: `Generic service error, error=${error.response.status}`,
        };
      } else {
        log.warn(`Unexpected service error, error==${error}`);
        const status = error.response.status || 500;
        error.response.data = {
          status,
          error: `Unexpected service error, error=${status}`,
          message: `Unexpected service error, error=${status}`,
        };
      }
    } else if (error.response) {
      const status = error.response.status || 500;
      log.warn(`Received generic service level error, status=${status}`);
      error.response.data = {
        status,
        error: `Generic service error (${status}). Please try again. If this problem persists please contact Quicken support`,
      };
    } else if (error.message && error.message === 'Network Error') {
      log.warn('Found network error');
      if (error.response) {
        error.response.data = { status: 500, error: 'Network Error (500). Please try again.' };
      }
    } else {
      log.warn(`Unexpected error response, error=${error}`);
    }

    if (error && error.config && error.config.headers) {
      requestAttempts.delete(error.config.headers['client-tid']);
    }

    return Promise.reject(error);
  },
);

// Dev Host Routing - routes requests to developer's devhost instance
// Configuration is managed via DevMenu -> Devhost Routing
// Only registered in non-production environments
if (isDevMode()) {
  qcsAxiosInstance.interceptors.request.use(
    (config) => {
      if (!config.url) return config;

      const fullUrl = new URL(config.url, config.baseURL || getEnvironmentConfig().services_url);
      const routingHeader = getRoutingHeaderForPath(fullUrl.pathname);

      if (routingHeader) {
        config.headers = config.headers || {};
        config.headers['qcs-routing'] = routingHeader;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );
}

AxiosFactory.register('qcs', qcsAxiosInstance);

export default AxiosFactory;

// /////////////////////////////////////////////////////////////////////////////
// QCS Refresh Token Helpers
// /////////////////////////////////////////////////////////////////////////////

let queuedQcsRequestsToRetry = [];
const requestAttempts = new Map();

const handleAuthorization = (config) => {
  let configOrPromise = config;

  if (isDemoMode()) {
    const demoModeWhiteList = featureFlagsSelectors.getFeatureFlag(store.getState(), 'demoModeWhiteList');
    const signature = `${config.method}:${config.url}`;
    const isAccepted =
      !demoModeWhiteList.size || demoModeWhiteList.some((regExp) => new RegExp(regExp).test(signature));
    if (!isAccepted) {
      log.debug('demoMode blocked: ', signature, config);
      return Promise.reject(Error('demoMode'));
    }
  }

  if (isSanitized(store.getState()) || sessionStorageEx.getSandbox()) {
    return Promise.reject(Error('sandbox'));
  }

  if (config.cancelToken && config.cancelToken.throwIfRequested) {
    config.cancelToken.throwIfRequested();
  }

  const attempts = requestAttempts.get(configOrPromise.headers['client-tid']) || 0;
  requestAttempts.set(configOrPromise.headers['client-tid'], attempts + 1);

  // use Authorization = null if the header is not required
  if (typeof configOrPromise.headers.Authorization === 'object') {
    // null or {...}
    delete configOrPromise.headers.Authorization;
  } else {
    const authSession = authSelectors.getAuthSession(store.getState());
    if (authSession && authSession.accessToken && !isAccessTokenExpired(authSession)) {
      configOrPromise.headers.Authorization = `Bearer ${authSession.accessToken}`;
      // we enforce qcs-dataset-id header to be always defined (qcs-dataset-id = null if the header is not required)
      if (typeof configOrPromise.headers['qcs-dataset-id'] === 'object') {
        // null or {...}
        delete configOrPromise.headers['qcs-dataset-id'];
      } else if (!configOrPromise.headers['qcs-dataset-id']) {
        if (authSession.datasetId) {
          configOrPromise.headers['qcs-dataset-id'] = authSession.datasetId;
        } else {
          log.error('NO DATASET ID IN AUTH SESSION');
          trackError(config, 'no dataset id');

          assert(
            false,
            `unauthorized request: no qcs-dataset-id \n${configOrPromise.method} ${scrubUrl(configOrPromise.url)}`,
          );

          configOrPromise = Promise.reject(
            Error(`No dataset id found in DB. ${configOrPromise.method} ${scrubUrl(configOrPromise.url)}`),
          );

          // TODO: trigger select dataset UI instead of logout
        }
      }
    } else if (authSession && !isRefreshTokenExpired(authSession) && authSession.refreshToken) {
      // use refresh token
      doRefreshToken(authSession.refreshToken);
      configOrPromise = resumeRequestAfterRefreshingQcsToken(configOrPromise);
    } else {
      // force user to login again
      log.log('NO VALID AUTH TOKENS - FORCE USER TO SIGN-IN');
      trackError(config, 'no valid auth');

      // assert(false, `auth error - force logout\n${config?.url}`);
      configOrPromise = Promise.reject(Error('valid auth tokens not found locally'));
      // store.dispatch(authActions.authLogout({ reason: 'AUTH_LOGOUT_UNAUTHORIZED' }));
    }
  }

  return configOrPromise;
};

function isRefreshTokenExpired(authSession) {
  if (!authSession.refreshTokenExpired) {
    return false;
  }
  const expiredTime = DateTime.fromISO(authSession.refreshTokenExpired);
  return expiredTime <= DateTime.local();
}

function isAccessTokenExpired(authSession) {
  // cannot tell if we don't have an expiration date so just return false
  if (!authSession.accessTokenExpired) {
    return false;
  }
  const expiredTime = DateTime.fromISO(authSession.accessTokenExpired);
  return expiredTime <= DateTime.local();
}

function doRefreshToken(refreshToken) {
  log.info('Refreshing token');
  new Promise((resolve, reject) =>
    store.dispatch(
      authActions.exchangeRefreshToken(
        {
          grantType: 'refreshToken',
          responseType: 'token',
          redirectUri: `${(getHostConfig() || getHostConfig('localhost')).redirect_uri}`,
          clientId: `${getEnvironmentConfig().client_id}`,
          clientSecret: `${getEnvironmentConfig().client_secret}`,
          refreshToken,
        },
        {
          resolve,
          reject,
          axiosConfig: {
            headers: {
              Authorization: null,
            },
          },
        },
      ),
    ),
  )
    .then((refreshTokenResponse) => afterTokenRefreshSucceeds(refreshTokenResponse))
    .catch((refreshTokenError) => afterTokenRefreshFails(refreshTokenError));
}

function resumeRequestAfterRefreshingQcsToken(config) {
  const resumeRequestPromise = new Promise((resumeRequestResolve, resumeRequestReject) => {
    const refreshSuccessCallback = (_refreshTokenResponse) => resumeRequestResolve(handleAuthorization(config));
    const refreshFailureCallback = (refreshTokenError) => resumeRequestReject(refreshTokenError);
    queuedQcsRequestsToRetry.push([refreshSuccessCallback, refreshFailureCallback]);
  });

  return resumeRequestPromise;
}

function retryRequestAfterRefreshingQcsToken(config) {
  const retryRequest = new Promise((retryResolve, retryReject) => {
    const retrySuccessCallback = (_refreshTokenResponse) => {
      qcsAxiosInstance
        .request(config)
        .then((retryResponse) => {
          retryResolve(retryResponse);
        })
        .catch((retryError) => {
          retryReject(retryError);
        });
    };
    const retryFailureCallback = (refreshTokenError) => {
      retryReject(refreshTokenError);
    };
    queuedQcsRequestsToRetry.push([retrySuccessCallback, retryFailureCallback]);
  });
  return retryRequest;
}

function clearQueuedQcsReqestsToRetry() {
  queuedQcsRequestsToRetry = [];
}

// Both settle paths capture the wait list before clearing it and only then invoke the callbacks.
// Resuming a parked request re-runs handleAuthorization synchronously, and when the exchanged
// token is unusable (already expired, missing) that pushes a fresh waiter behind a new refresh;
// clearing after the callbacks would drop it and the request would never settle.
function afterTokenRefreshSucceeds(refreshTokenResponse) {
  log.info('Refreshing token - succeed');

  const waitList = queuedQcsRequestsToRetry;
  clearQueuedQcsReqestsToRetry();
  waitList.forEach(([resolveWaiter]) => {
    resolveWaiter(refreshTokenResponse);
  });
}

function afterTokenRefreshFails(error) {
  log.error(`Refreshing token - failed, error=${error}`);

  const waitList = queuedQcsRequestsToRetry;
  clearQueuedQcsReqestsToRetry();
  waitList.forEach(([, rejectWaiter]) => {
    rejectWaiter(error);
  });

  if ([400, 401, 406, 500].includes(error?.response?.status)) {
    store.dispatch(authActions.invalidateRefreshToken(undefined, { context: 'axios refresh failed' }));
  }
}

const trackError = (config, description) => {
  const url = new URL(config.url);
  const tag = normalizePath(url.pathname);
  tracker.track(tracker.events.connectivity, {
    status: 'error',
    method: config.method && config.method.toUpperCase(),
    host: url && url.hostname,
    path: tag,
    tag,
    description,
    details: description,
    error_code: description,
    error_sub_code: description,
  });
};

const errorsBlackList = [
  /.*\[Network Error:undefined\]/, // no connection
  /.*\[Request aborted:ECONNABORTED\]/, // browser 'stop' aborts requests
  /.*\[timeout exceeded:ECONNABORTED\]/, // browser 'stop' aborts requests
  /.*\[canceled:ERR_CANCELED\]/, // AbortController/axios cancellation (e.g. expected SSE stream close)
  '/institution-logins [409:QCS-0409]',
  '/institution-logins/xxx [409:QCS-0409]',
  '/analysis/saga/xxx [404:undefined]',
  '/pfm-importer/import [403:QCS-0403-2]', // Bad File: Could not find fields [Amount, Category, Date, Payee, Tags]' in input
  '/job-statuses [401:QCS-0401-5]', // Trying to access a deleted dataset.
  '/jwt-sso/token-redirect [401:QCS-0401-7]', // QCS Unauthorized Access - Username requires verification for community access
  '/oauth/token [401:QCS-0401-19]', // refresh token is revoked due to password change
  /.*\[401:QCS-0401-2\]/, // The token expired, was revoked, or the token ID is incorrect.
  /\/institution-logins.* \[429:QCS-0429-2\]/, // QCS-AASR-1 - Please try again later. If the problem persists, contact Customer Care for support.
];

const reportRequestFailure = (message, request, response, tag, code) => {
  if (request) {
    const url = new URL(request.url);
    const path = normalizePath(url.pathname, url.hostname);
    const context = `${path} [${tag}]`; // we use context as black list key -> keep errorsBlackList up to date
    if (errorsBlackList.find((mask) => (typeof mask === 'string' ? context.includes(mask) : mask.test?.(context)))) {
      log.warn(`network error blacklisted: '${context}`);
    } else {
      log.warn(`network error will be send to bugsnag: '${context}`);
      const error = new Error(message);
      error.name = 'HTTPError';

      crashReporterInterface.reportError(
        error,
        { network: getRequestFailureMetadata({ code, config: request, message, response }) },
        { context },
      );
    }
  }
};
